// AI Coach Service - Real intelligent recommendations based on user performance
// IMPORTANT: AI uses PERFORMANCE (scores, mastery, weak areas) - NOT time - for recommendations

import { cloudSave } from './cloudSave';
import { realTimeSync } from './realTimeSync';
import { userAuth } from './userAuth';
import { adaptiveDifficultyService } from './adaptiveDifficultyService';
import { logger } from '../utils/logger';
import { requestTracing } from './requestTracing';
import { deferredWeakAreaQueue } from './deferredWeakAreaQueue';
import { unifiedState } from './unifiedStateManager';
import {
  RECOMMENDATION_IGNORE_THRESHOLD,
  WEAK_AREA_AI_EXAM_THRESHOLD,
} from '../config/recommendationConfig';


export interface TestResult {
  testId: string;
  testName: string;
  score: number;
  totalQuestions: number;
  percentage: number;
  date: string;
}

export interface TestRecommendation {
  testId: string;
  testName: string;
  reason: string;
  priority: 'critical' | 'high' | 'medium';
  score: number;
  ignoreCount: number;
}

export interface AIInsight {
  type: 'mistake' | 'strength' | 'recommendation';
  message: string;
  priority: 'red' | 'amber' | 'green';
  testId?: string;
  explanation?: string;
  frequency?: number;
  lastOccurrence?: string;
  score?: number; // Test score percentage (0-100)
  items?: Array<{ testName: string; testId: string; score: number }>; // Multiple items for yellow/green boxes
}

// Test metadata
export const TEST_METADATA: Record<string, { name: string; category: string }> = {
  'traffic-lights-signals': { name: 'Traffic Lights & Signals', category: 'signals' },
  'priority-rules': { name: 'Priority & Right of Way', category: 'rules' },
  'hazard-perception': { name: 'Hazard Perception', category: 'safety' },
  'speed-safety': { name: 'Speed & Safety', category: 'safety' },
  'bicycle-interactions': { name: 'Bicycle Interactions', category: 'interactions' },
  'roundabout-rules': { name: 'Roundabout Rules', category: 'rules' },
  'tram-interactions': { name: 'Tram Interactions', category: 'interactions' },
  'pedestrian-crossings': { name: 'Pedestrian Crossings', category: 'interactions' },
  'construction-zones': { name: 'Construction Zones', category: 'zones' },
  'weather-conditions': { name: 'Weather Conditions', category: 'safety' },
  'road-signs': { name: 'Road Signs', category: 'signs' },
  'motorway-rules': { name: 'Motorway Rules', category: 'rules' },
  'vehicle-knowledge': { name: 'Vehicle Knowledge', category: 'vehicles' },
  'parking-rules': { name: 'Parking Rules', category: 'rules' },
  'environmental': { name: 'Environmental Zones', category: 'zones' },
  'technology-safety': { name: 'Technology & Safety', category: 'technology' },
  'alcohol-drugs': { name: 'Alcohol & Drugs', category: 'safety' },
  'fatigue-rest': { name: 'Fatigue & Rest', category: 'safety' },
  'emergency-procedures': { name: 'Emergency Procedures', category: 'safety' },
  'insight-practice': { name: 'Insight Practice', category: 'advanced' },
};

// Default beginner path for new users
const BEGINNER_PATH = ['traffic-lights-signals', 'priority-rules', 'hazard-perception', 'speed-safety'];

/** First 5 tests in display order; algorithm uses "next in order" until user has 5 completed, then switches to weak-area logic. */
const _FIRST_FIVE_IN_ORDER = ['traffic-lights-signals', 'priority-rules', 'hazard-perception', 'speed-safety', 'bicycle-interactions'];

class AICoachService {
  // Get translated test name
  private getTranslatedTestName(testId: string, translate: (key: string) => string): string {
    const testNameMap: Record<string, string> = {
      'traffic-lights-signals': translate('testNames.trafficLightsSignals'),
      'priority-rules': translate('testNames.priorityRules'),
      'hazard-perception': translate('testNames.hazardPerception'),
      'speed-safety': translate('testNames.speedSafety'),
      'bicycle-interactions': translate('testNames.bicycleInteractions'),
      'roundabout-rules': translate('testNames.roundaboutRules'),
      'tram-interactions': translate('testNames.tramInteractions'),
      'pedestrian-crossings': translate('testNames.pedestrianCrossings'),
      'construction-zones': translate('testNames.constructionZones'),
      'weather-conditions': translate('testNames.weatherConditions'),
      'road-signs': translate('testNames.roadSigns'),
      'motorway-rules': translate('testNames.motorwayRules'),
      'vehicle-knowledge': translate('testNames.vehicleKnowledge'),
      'parking-rules': translate('testNames.parkingRules'),
      'environmental': translate('testNames.environmental'),
      'technology-safety': translate('testNames.technologySafety'),
      'alcohol-drugs': translate('testNames.alcoholDrugs'),
      'fatigue-rest': translate('testNames.fatigueRest'),
      'emergency-procedures': translate('testNames.emergencyProcedures'),
    };
    return testNameMap[testId] || TEST_METADATA[testId]?.name || testId.replace(/-/g, ' ');
  }

  // Save test result - optimized for speed (non-blocking cloud save)
  // Enhanced with request tracing (Netflix/Duolingo style)
  async saveTestResult(result: TestResult): Promise<void> {
    return await requestTracing.trace('saveTestResult', async () => {
      // Save to localStorage immediately (synchronous, fast)
      const history = this.getTestHistory();
      history.push(result);
      localStorage.setItem('testHistory', JSON.stringify(history));
      
      // Clear ignore count immediately (synchronous, fast)
      const currentRec = this.getTopRecommendation();
      if (currentRec.testId === result.testId) {
        this.clearIgnoreCount(result.testId);
      }

      // Invalidate recommendation caches so next request reflects new scores/mastery
      try {
        const { memoizedRecommendations } = require('./memoizedRecommendations');
        const currentUser = userAuth.getCurrentUser();
        memoizedRecommendations.invalidate(currentUser?.id || 'guest');
      } catch {
        // ignore
      }
      try {
        const enhanced = null;
        if (enhanced && typeof enhanced.invalidateSessionCache === 'function') {
          enhanced.invalidateSessionCache();
        }
      } catch {
        // ignore
      }

      // Save to cloud with real-time sync (don't block UI)
      const currentUser = userAuth.getCurrentUser();
      if (currentUser && !currentUser.isGuest) {
        const progressData = {
          testHistory: history,
          studyTime: this.getStudyTime(),
          averageScore: this.getCombinedAverage()
        };
        // Use real-time sync (handles conflict resolution automatically)
        realTimeSync.saveProgress(currentUser.id, progressData).catch(err => {
          logger.error('Real-time sync failed (non-blocking):', err);
          // Fallback to legacy cloudSave
          cloudSave.saveProgress(currentUser.id, progressData).catch(fallbackErr => {
            logger.error('Cloud save fallback failed:', fallbackErr);
          });
        });
      }
    }, { testId: result.testId, score: result.percentage });
  }

  // Get all test history (guarded: malformed localStorage can throw)
  getTestHistory(): TestResult[] {
    try {
      const history = localStorage.getItem('testHistory');
      if (!history) return [];
      const parsed = JSON.parse(history);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  // Calculate average score per test
  getTestScores(): Record<string, { average: number; count: number; lastScore: number }> {
    const history = this.getTestHistory();
    const scores: Record<string, number[]> = {};
    
    history.forEach(result => {
      if (!scores[result.testId]) {
        scores[result.testId] = [];
      }
      scores[result.testId].push(result.percentage);
    });

    const testScores: Record<string, { average: number; count: number; lastScore: number }> = {};
    Object.keys(scores).forEach(testId => {
      const testScoreArray = scores[testId];
      const average = testScoreArray.reduce((a, b) => a + b, 0) / testScoreArray.length;
      testScores[testId] = {
        average: Math.round(average),
        count: testScoreArray.length,
        lastScore: testScoreArray[testScoreArray.length - 1]
      };
    });

    return testScores;
  }

  // Track recommendation ignore (persist + analytics for future refinement)
  trackIgnore(testId: string): void {
    const ignores = this.getIgnoreCounts();
    ignores[testId] = (ignores[testId] || 0) + 1;
    localStorage.setItem('recommendationIgnores', JSON.stringify(ignores));
    try {
      const { userAuth } = require('./userAuth');
      const { backgroundJobs } = require('./backgroundJobs');
      const user = userAuth.getCurrentUser();
      if (user?.id && !user.isGuest) {
        backgroundJobs.trackEvent(user.id, 'recommendation_ignored', {
          testId,
          ignoreCount: ignores[testId],
          timestamp: Date.now()
        });
      }
    } catch {
      // ignore
    }
  }

  // Get ignore counts (guarded: malformed localStorage can throw)
  getIgnoreCounts(): Record<string, number> {
    try {
      const ignores = localStorage.getItem('recommendationIgnores');
      if (!ignores) return {};
      const parsed = JSON.parse(ignores);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }

  // Clear ignore count
  clearIgnoreCount(testId: string): void {
    const ignores = this.getIgnoreCounts();
    delete ignores[testId];
    localStorage.setItem('recommendationIgnores', JSON.stringify(ignores));
  }

  /**
   * Get top recommendation = "best next test".
   * PRIORITY ORDER: 1) AI Exam (if unlocked and weak-area count < threshold)
   * 2) Enhanced recommendation service (weak area, spaced repetition, velocity, retention, category balance, time decay; filter ignoreCount >= 3; tie-breakers: lower score, lower mastery, fewer ignores)
   * 3) Weak areas (getFocusAreas; score/mastery < 60%; tie-breakers: lowest score, lowest mastery, fewer ignores, then first in BEGINNER_PATH)
   * 4) Queued weak areas (deferredWeakAreaQueue.getNextWeakArea())
   * 5) New user (BEGINNER_PATH[0]; skip *-2 in first five)
   * 6) Fallback (untaken first, first-five order, lowest score; exclude current/completed; respect ignoreCount)
   * First-five and Netflix-style rules apply in getNextTest(); no repeats; instance resume/retake handled in AI Exam flow.
   */
  getTopRecommendation(translate?: (key: string) => string): TestRecommendation {
    console.log('getTopRecommendation called');
    const focusAreas = unifiedState.getFocusAreas();
    const weakBelow60 = focusAreas.filter(a => !a.mastered && a.score < 60);
    const hasManyWeakAreas = weakBelow60.length >= WEAK_AREA_AI_EXAM_THRESHOLD;
    
    console.log('getTopRecommendation debug:', { focusAreas, weakBelow60, hasManyWeakAreas });

    // AI Exam vs weak-area balance: prefer weak areas when user has ≥3 weak areas <60%
    try {
      const { aiExamRegenerationService } = require('./aiExamRegenerationService');
      const aiExamStatus = unifiedState.getAIExamStatus();
      const canRecommendAIExam = aiExamStatus.canUnlock && aiExamRegenerationService.hasSufficientNewLearning();
      if (canRecommendAIExam && !hasManyWeakAreas) {
        logger.debug('Recommendation: AI Exam (unlocked, weak areas below threshold)', {
          weakCount: weakBelow60.length,
          threshold: WEAK_AREA_AI_EXAM_THRESHOLD
        });
        return {
          testId: 'ai-exam',
          testName: (() => {
            if (!translate) return 'AI Exam';
            const t = translate('mockExam.aiExamTitle');
            return t && t !== 'mockExam.aiExamTitle' ? t : 'AI Exam';
          })(),
          reason: translate ? translate('mockExam.readyForAIExam') || 'You’re ready for AI Exam' : 'You’re ready for AI Exam',
          priority: 'critical',
          score: 0,
          ignoreCount: 0
        };
      }
      if (canRecommendAIExam && hasManyWeakAreas) {
        logger.debug('Recommendation: weak area preferred over AI Exam (≥ threshold)', {
          weakCount: weakBelow60.length,
          threshold: WEAK_AREA_AI_EXAM_THRESHOLD
        });
      }
    } catch (error) {
      logger.debug('AI Exam recommendation check failed:', error);
    }

    // Use enhanced recommendation service if available (with session cache and immediate fallback)
    const enhancedService = null;
    if (enhancedService) {
      try {
        const enhanced = enhancedService.getEnhancedRecommendation(translate);
        if (enhanced && enhanced.testId) {
          return {
            testId: enhanced.testId,
            testName: enhanced.testName,
            reason: enhanced.reason,
            priority: enhanced.priority,
            score: enhanced.score,
            ignoreCount: enhanced.ignoreCount
          };
        }
      } catch (error) {
        logger.debug('Enhanced recommendation failed, using base logic:', error);
      }
    }

    return this.getTopRecommendationBase(translate);
  }

  /**
   * Base recommendation only (no AI Exam override, no enhanced service).
   * Used by enhanced service fallback to avoid recursion.
   */
  getBaseRecommendation(translate?: (key: string) => string): TestRecommendation {
    return this.getTopRecommendationBase(translate);
  }

  // Original recommendation logic (now as separate method)
  private getTopRecommendationBase(translate?: (key: string) => string): TestRecommendation {
    console.log('getTopRecommendationBase called');
    const testScores = this.getTestScores();
    const ignores = this.getIgnoreCounts();
    const testHistory = this.getTestHistory();

    console.log('getTopRecommendationBase debug:', { testScores, ignores, testHistory });

    // PRIORITY 1: Check for focus areas immediately (no 5-test requirement)
    // Get focus areas from unified state (includes all tests with score < 60%)
    const focusAreas = unifiedState.getFocusAreas();
    if (focusAreas.length > 0) {
      // Sort by score (lowest first = weakest = highest priority)
      const sortedFocusAreas = [...focusAreas]
        .filter(area => !area.mastered && area.score < 60)
        .sort((a, b) => a.score - b.score);
      
      if (sortedFocusAreas.length > 0) {
        const weakestArea = sortedFocusAreas[0];
        const testName = translate ? this.getTranslatedTestName(weakestArea.testId, translate) : weakestArea.testName;
        const recommendation = {
          testId: weakestArea.testId,
          testName,
          reason: translate ? translate('practice.practiceFocusAreaNext') || 'Practice Focus Area Next' : 'Practice Focus Area Next',
          priority: 'critical' as const,
          score: weakestArea.score,
          ignoreCount: ignores[weakestArea.testId] || 0
        };
        console.log('getTopRecommendationBase returning focus area:', recommendation);
        return recommendation;
      }
    }
    
    // PRIORITY 2: Check queued focus areas from Focus Session
    const queuedWeakArea = deferredWeakAreaQueue.getNextWeakArea();
    if (queuedWeakArea) {
      const testName = translate ? this.getTranslatedTestName(queuedWeakArea.testId, translate) : queuedWeakArea.testName;
      return {
        testId: queuedWeakArea.testId,
        testName,
        reason: translate ? translate('practice.practiceFocusAreaNext') || 'Practice Focus Area Next' : 'Practice Focus Area Next',
        priority: 'critical',
        score: queuedWeakArea.score,
        ignoreCount: ignores[queuedWeakArea.testId] || 0
      };
    }

    // PRIORITY 3: New user - recommend beginner path (only if no focus areas exist)
    if (Object.keys(testScores).length === 0) {
      const recommendation = {
        testId: 'traffic-lights-signals',
        testName: translate ? translate('testNames.trafficLightsSignals') || 'Traffic Lights & Signals' : 'Traffic Lights & Signals',
        reason: translate ? translate('practice.startWithBeginner') || 'Start with beginner test' : 'Start with beginner test',
        priority: 'medium' as const,
        score: 0,
        ignoreCount: 0
      };
      console.log('getTopRecommendationBase returning beginner test:', recommendation);
      return recommendation;
    }

    // STEP 1: Identify ALL weak areas (score < 60% OR mastery < 60%)
    const weakAreas: Array<{ 
      testId: string; 
      score: number; 
      mastery: number;
      ignoreCount: number;
      isWeak: boolean; // True if score < 60 OR mastery < 60
    }> = [];
    
    Object.keys(TEST_METADATA).forEach(testId => {
      const testData = testScores[testId];
      const mastery = adaptiveDifficultyService.getTopicMastery(testId, testHistory);
      const score = testData?.average || 0;
      
      // A test is "weak" if:
      // - Score < 60% (has been taken and scored low)
      // - OR Mastery < 60% (adaptive learning identifies it as weak)
      // - OR Not practiced yet (score = 0, but we'll handle this separately)
      const isWeak = (score > 0 && score < 60) || (mastery > 0 && mastery < 60);
      
        weakAreas.push({
          testId,
        score,
        mastery,
        ignoreCount: ignores[testId] || 0,
        isWeak
        });
    });

    // STEP 2: Separate weak areas from other tests (use configurable ignore threshold)
    const actualWeakAreas = weakAreas.filter(area => area.isWeak && area.ignoreCount < RECOMMENDATION_IGNORE_THRESHOLD);
    const otherTests = weakAreas.filter(area => !area.isWeak || area.ignoreCount >= RECOMMENDATION_IGNORE_THRESHOLD);

    // STEP 3: STRICT PRIORITY - Only recommend from weak areas if they exist
    let candidateAreas: typeof weakAreas = [];
    
    if (actualWeakAreas.length > 0) {
      // PRIORITY RULE: Weak areas exist - ONLY recommend from weak areas
      candidateAreas = actualWeakAreas;
      
      // Sort weak areas by:
      // 1. Lowest score first (worst performance)
      // 2. Lowest mastery first (weakest topic)
      // 3. Less ignored first
      candidateAreas.sort((a, b) => {
        // First: by score (lower = worse = higher priority)
        if (a.score !== b.score && a.score > 0 && b.score > 0) {
          return a.score - b.score;
        }
        
        // Second: by mastery (lower = weaker = higher priority)
        if (a.mastery !== b.mastery && a.mastery > 0 && b.mastery > 0) {
          return a.mastery - b.mastery;
        }
        
        // Third: by ignore count (less ignored = higher priority)
      if (a.ignoreCount !== b.ignoreCount) {
        return a.ignoreCount - b.ignoreCount;
      }

        // Tie-breaker: prefer tests with actual scores over unpracticed
        if (a.score > 0 && b.score === 0) return -1;
        if (a.score === 0 && b.score > 0) return 1;
        // Final tie-breaker: first in configured priority path (BEGINNER_PATH)
        const orderA = BEGINNER_PATH.indexOf(a.testId);
        const orderB = BEGINNER_PATH.indexOf(b.testId);
        if (orderA >= 0 && orderB >= 0) return orderA - orderB;
        if (orderA >= 0) return -1;
        if (orderB >= 0) return 1;
        return 0;
      });
    } else {
      // NO weak areas exist - only then recommend other tests
      // This includes:
      // - Unpracticed tests (score = 0, mastery = 0)
      // - Strong tests (score >= 60, mastery >= 60)
      candidateAreas = otherTests;
      
      // Sort other tests by:
      // 1. Unpracticed tests first (beginner path priority)
      // 2. Lower scores first (even if >= 60)
      // 3. Less ignored first
      candidateAreas.sort((a, b) => {
        // Unpracticed tests (score = 0) come first
        if (a.score === 0 && b.score > 0) return -1;
        if (a.score > 0 && b.score === 0) return 1;
        
        // Beginner path priority for unpracticed
        if (a.score === 0 && b.score === 0) {
          const aInPath = BEGINNER_PATH.includes(a.testId);
          const bInPath = BEGINNER_PATH.includes(b.testId);
          if (aInPath && !bInPath) return -1;
          if (!aInPath && bInPath) return 1;
        }
        
        // Then by score (lower first, even if >= 60)
        if (a.score !== b.score) {
      return a.score - b.score;
        }
        
        // Then by ignore count
        return a.ignoreCount - b.ignoreCount;
      });
    }

    // STEP 4: Get the top recommendation
    const topRecommendation = candidateAreas[0];
    if (!topRecommendation) {
      // Fallback (shouldn't happen, but just in case)
      return {
        testId: BEGINNER_PATH[0],
        testName: TEST_METADATA[BEGINNER_PATH[0]].name,
        reason: 'Start your learning journey',
        priority: 'high',
        score: 0,
        ignoreCount: 0
      };
    }

    const testName = translate ? this.getTranslatedTestName(topRecommendation.testId, translate) : TEST_METADATA[topRecommendation.testId].name;
    
    // STEP 5: Determine priority and short, motivational reason (no raw thresholds in UI)
    let priority: 'critical' | 'high' | 'medium' = 'medium';
    let shortLabel: string;

    if (topRecommendation.isWeak) {
      priority = 'critical';
      if (topRecommendation.score < 40 || topRecommendation.mastery < 40) {
        shortLabel = translate ? translate('recommendations.focusHere') || 'Focus here first' : 'Focus here first';
      } else {
        shortLabel = translate ? translate('recommendations.weakArea') || 'Weak area' : 'Weak area';
      }
    } else {
      if (topRecommendation.score === 0) {
        priority = 'high';
        shortLabel = translate ? translate('recommendations.startHere') || 'Start here' : 'Start here';
      } else if (topRecommendation.score < 88) {
        priority = 'high';
        shortLabel = translate ? translate('recommendations.almostThere') || 'Almost there' : 'Almost there';
      } else {
        priority = 'medium';
        shortLabel = translate ? translate('recommendations.goodProgress') || 'Good progress' : 'Good progress';
      }
    }
    const reason = `${testName} – ${shortLabel}`;

    return {
      testId: topRecommendation.testId,
      testName,
      reason,
      priority,
      score: topRecommendation.score,
      ignoreCount: topRecommendation.ignoreCount
    };
  }

  // Get contextual reasoning for recommendations
  private getContextualReason(weakArea: { testId: string; score: number; ignoreCount: number }, testScores: any, _translate?: (key: string) => string): string {
    const { testId, score } = weakArea;
    const testName = TEST_METADATA[testId].name;
    
    // Count total tests completed
    const completedTests = Object.keys(testScores).length;
    const totalTests = Object.keys(TEST_METADATA).length;
    const progressPercentage = Math.round((completedTests / totalTests) * 100);
    
    // Early learner context
    if (completedTests < 5) {
      if (score === 0) {
        return `Perfect starting point - ${testName} is fundamental`;
      }
      return `Build your foundation with ${testName}`;
    }
    
    // Mid-progress context
    if (completedTests < 15) {
      if (score < 60) {
        return `Focus on ${testName} - you're ${progressPercentage}% through all topics`;
      }
      return `Strengthen ${testName} - almost halfway there!`;
    }
    
    // Advanced learner context
    if (score < 60) {
      return `Final push on ${testName} - you're ${progressPercentage}% complete`;
    }
    
    if (score < 88) {
      return `Polish ${testName} - you're almost exam-ready!`;
    }
    
    return `Review ${testName} to maintain your ${score}% score`;
  }

  // Get AI Insights for dashboard - ALWAYS 3 boxes (RED, YELLOW, GREEN)
  getAIInsights(translate?: (key: string) => string): AIInsight[] {
    const testScores = this.getTestScores();
    const testHistory = this.getTestHistory();
    const recommendation = this.getTopRecommendation(translate); // Pass translate function
    
    // IMPORTANT: Only show mock exam if user has test history AND all tests are completed
    const hasAnyTestHistory = testHistory && testHistory.length > 0;
    const completedTestIds = Object.keys(testScores);
    const allTestIds = Object.keys(TEST_METADATA);
    const allTestsCompleted = hasAnyTestHistory && 
                              allTestIds.length > 0 && 
                              completedTestIds.length > 0 &&
                              allTestIds.every(testId => completedTestIds.includes(testId));
    
    const isReadyForMockExam = allTestsCompleted;

    // Helper to get short memory context with translations
    const getMemoryContext = (testId: string): string => {
      if (!testId || testId === 'mock-exam') return '';
      
      // Find past struggles with this test
      const pastTests = testHistory.filter(t => t.testId === testId && t.percentage < 60);
      if (pastTests.length === 0) return '';
      
      // Get most recent struggle
      const lastStruggle = pastTests.sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      )[0];
      
      const daysAgo = Math.floor(
        (Date.now() - new Date(lastStruggle.date).getTime()) / (1000 * 60 * 60 * 24)
      );
      
      // Get translation function
      const t = translate || ((key: string) => key);
      const getTranslated = (key: string, replacements: Record<string, string | number> = {}) => {
        const translated = t(key);
        if (!translated || translated === key) return key;
        let result = translated;
        Object.entries(replacements).forEach(([placeholder, value]) => {
          result = result.replace(`{${placeholder}}`, String(value));
        });
        return result;
      };
      
      // Show if struggled within 7 days (mobile filtering done in component)
      if (daysAgo <= 7) {
        if (daysAgo === 0) {
          return getTranslated('dashboard.memoryContext.struggledToday', { score: lastStruggle.percentage });
        } else if (daysAgo === 1) {
          return getTranslated('dashboard.memoryContext.struggledYesterday', { score: lastStruggle.percentage });
        } else {
          return getTranslated('dashboard.memoryContext.struggledDaysAgo', { days: daysAgo, score: lastStruggle.percentage });
        }
      }
      
      return '';
    };

    const insights: AIInsight[] = [];

    // 1. RED BOX - Mock Exam CTA if ready, otherwise top recommendation
    if (isReadyForMockExam) {
      insights.push({
        type: 'recommendation',
        message: translate ? translate('dashboard.startMockExam') : 'Start Mock Exam',
        priority: 'red',
        testId: 'mock-exam',
        explanation: translate ? translate('dashboard.readyToTest') : 'Ready to test your knowledge!',
        score: undefined // Mock exam doesn't have a score
      });
    } else {
        const redBoxScore = testScores[recommendation.testId]?.average || 0;
        const memoryContext = getMemoryContext(recommendation.testId);
        insights.push({
        type: 'recommendation',
        message: recommendation.testName,
        priority: 'red',
        testId: recommendation.testId,
        explanation: memoryContext || '', // Short memory context if available
        score: redBoxScore
      });
    }

    // 2. YELLOW BOX - Second priority (ALWAYS) - Show only one test name
    const secondPriority = this.getSecondPriority(testScores, recommendation.testId, translate);
    const yellowBoxScore = testScores[secondPriority.testId]?.average || 0;
    const yellowMemoryContext = getMemoryContext(secondPriority.testId);
    
    insights.push({
      type: 'mistake',
      message: secondPriority.testName,
      priority: 'amber',
      testId: secondPriority.testId,
      explanation: yellowMemoryContext || secondPriority.explanation,
      score: yellowBoxScore
    });

    // 3. GREEN BOX - Always show strongest test area (never show "Goal Crusher" or daily goal messages)
    // Show only one test name - the strongest area
    // IMPORTANT: Exclude the red box test and yellow box test to avoid showing the same test
    const redBoxTestId = isReadyForMockExam ? 'mock-exam' : recommendation.testId;
    const yellowBoxTestId = secondPriority.testId;
    const allTestScores = Object.keys(TEST_METADATA)
      .map(testId => ({
        testId,
        score: testScores[testId]?.average || 0
      }))
      .filter(t => t.testId !== redBoxTestId && t.testId !== yellowBoxTestId) // Exclude red and yellow box tests
      .sort((a, b) => b.score - a.score) // Sort by score descending (highest first)
      .filter(t => t.score > 0); // Only include tests with scores > 0 (strong areas)
    
    let greenTestName = '';
    let greenTestId = '';
    let greenBoxScore = 0;
    
    // Show strongest test area, excluding red and yellow box tests
    if (allTestScores.length > 0) {
      const firstStrong = allTestScores[0];
      greenTestName = translate ? this.getTranslatedTestName(firstStrong.testId, translate) : TEST_METADATA[firstStrong.testId].name;
      greenTestId = firstStrong.testId;
      greenBoxScore = firstStrong.score;
    } else {
      // Fallback for new users (no test scores yet) - show next test in beginner path
      const excludedTestIds = [redBoxTestId, yellowBoxTestId];
      const beginnerPathTests = BEGINNER_PATH.filter(id => !excludedTestIds.includes(id));
      
      if (beginnerPathTests.length > 0) {
        const nextBeginnerTestId = beginnerPathTests[0];
        greenTestName = translate ? this.getTranslatedTestName(nextBeginnerTestId, translate) : TEST_METADATA[nextBeginnerTestId].name;
        greenTestId = nextBeginnerTestId;
        greenBoxScore = 0;
      } else {
        // If all beginner path tests are used, show any other test (excluding red and yellow)
        const fallbackTests = Object.keys(TEST_METADATA).filter(id => !excludedTestIds.includes(id));
        if (fallbackTests.length > 0) {
          const fallbackTestId = fallbackTests[0];
          greenTestName = translate ? this.getTranslatedTestName(fallbackTestId, translate) : TEST_METADATA[fallbackTestId].name;
          greenTestId = fallbackTestId;
          greenBoxScore = 0;
        }
      }
    }
    
    const greenMemoryContext = getMemoryContext(greenTestId);
    insights.push({
      type: 'strength',
      message: greenTestName,
      priority: 'green',
      testId: greenTestId,
      explanation: greenMemoryContext || '', // Short memory context if available
      score: greenBoxScore
    });

    return insights; // Always return exactly 3
  }

  // Get second priority for YELLOW box
  private getSecondPriority(testScores: any, excludeTestId: string, translate?: (key: string) => string): { testName: string; testId: string; explanation: string } {
    // Find second weakest area (not the top recommendation)
    const availableTests = Object.keys(TEST_METADATA).filter(id => id !== excludeTestId);
    
    if (availableTests.length === 0) {
      return {
        testName: translate ? translate('dashboard.practiceMore') : 'Practice More',
        testId: 'traffic-lights-signals',
        explanation: translate ? translate('dashboard.continueBuilding') : 'Continue building your foundation'
      };
    }

    // Sort by score (lowest first)
    const sortedTests = availableTests
      .map(testId => ({
        testId,
        score: testScores[testId]?.average || 0
      }))
      .sort((a, b) => a.score - b.score);

    const secondWeak = sortedTests[0];
    const testName = translate ? this.getTranslatedTestName(secondWeak.testId, translate) : TEST_METADATA[secondWeak.testId].name;
    
    let explanation = '';
    if (secondWeak.score === 0) {
      explanation = translate ? translate('dashboard.notPracticedYet') : 'Not practiced yet - good next step';
    } else if (secondWeak.score < 60) {
      explanation = translate 
        ? `${secondWeak.score}% - ${translate('dashboard.needsImprovement') || 'needs improvement'}`
        : `${secondWeak.score}% - needs improvement`;
    } else if (secondWeak.score < 80) {
      explanation = translate
        ? `${secondWeak.score}% - ${translate('dashboard.almostReady') || 'almost ready'}`
        : `${secondWeak.score}% - almost ready`;
    } else {
      explanation = translate
        ? `${secondWeak.score}% - ${translate('dashboard.goodProgress') || 'good progress'}`
        : `${secondWeak.score}% - good progress`;
    }

    return {
      testName,
      testId: secondWeak.testId,
      explanation
    };
  }

  // Get most important achievement or score for GREEN box
  // Priority: 1) Daily goal completion, 2) Daily goal progress, 3) Newly unlocked achievements, 4) Best performing test
  private getMostImportantAchievement(translate?: (key: string) => string): { message: string; testId?: string; explanation: string } {
    try {
      // Priority 1: Check daily goal completion/progress
      try {
        const { dailyChallengesService } = require('./dailyChallengesService');
        const challengeProgress = dailyChallengesService.getChallengeProgress();
        const challenges = dailyChallengesService.getTodaysChallenges();
        const questionsChallenge = challenges.find((c: any) => c.type === 'questions');
        
        if (questionsChallenge) {
          const goalTarget = questionsChallenge.target || 10;
          const questionsAnswered = challengeProgress.completed > 0 
            ? Math.floor((challengeProgress.progress / 100) * goalTarget)
            : 0;
          
          // If daily goal is completed (100% progress)
          if (challengeProgress.progress >= 100) {
            return {
              message: translate ? translate('dashboard.goalCrusher') || 'Goal Crusher' : 'Goal Crusher',
              explanation: translate 
                ? translate('dashboard.goalCrusherDescription')?.replace('{target}', goalTarget.toString()) || `You've completed your daily goal of ${goalTarget} questions!`
                : `You've completed your daily goal of ${goalTarget} questions!`
            };
          }
          
          // If daily goal is in progress (show progress)
          if (challengeProgress.progress > 0) {
            const remaining = goalTarget - questionsAnswered;
            return {
              message: translate ? translate('dashboard.dailyGoal') || 'Daily Goal' : 'Daily Goal',
              explanation: translate
                ? translate('dashboard.dailyGoalProgress')?.replace('{answered}', questionsAnswered.toString()).replace('{target}', goalTarget.toString()).replace('{remaining}', remaining.toString()) || `${questionsAnswered}/${goalTarget} questions - ${remaining} more to go!`
                : `${questionsAnswered}/${goalTarget} questions - ${remaining} more to go!`
            };
          }
        }
      } catch (dailyGoalError) {
        // If daily goal service fails, continue to other priorities
        logger.debug('Daily goal check failed, continuing to other priorities:', dailyGoalError);
      }
      
      // Priority 2: Find most recently unlocked achievement (if any)
      const { achievementService } = require('./achievementService');
      const achievements = achievementService.getAchievements();
      
      const unlockedAchievements = achievements.filter((a: any) => a.unlocked && a.unlockedDate);
      if (unlockedAchievements.length > 0) {
        // Sort by unlock date (most recent first)
        unlockedAchievements.sort((a: any, b: any) => {
          const dateA = a.unlockedDate ? new Date(a.unlockedDate).getTime() : 0;
          const dateB = b.unlockedDate ? new Date(b.unlockedDate).getTime() : 0;
          return dateB - dateA; // Most recent first
        });
        
        const mostRecent = unlockedAchievements[0];
        return {
          message: mostRecent.name,
          explanation: mostRecent.description
        };
      }
      
      // Priority 3: Find highest progress achievement (closest to unlocking)
      const lockedAchievements = achievements.filter((a: any) => !a.unlocked && a.progress > 0);
      if (lockedAchievements.length > 0) {
        // Sort by progress (highest first)
        lockedAchievements.sort((a: any, b: any) => b.progress - a.progress);
        const closestAchievement = lockedAchievements[0];
        
        // Only show if progress is significant (>= 25%)
        if (closestAchievement.progress >= 25) {
          const progressText = `${Math.round(closestAchievement.progress)}% complete`;
          
          return {
            message: closestAchievement.name,
            explanation: `${progressText} - ${closestAchievement.description}`
          };
        }
      }
      
      // Priority 4: Show best performing test score (fallback to strength area)
      const testScores = this.getTestScores();
      const bestTest = Object.keys(TEST_METADATA)
        .map(testId => ({
          testId,
          score: testScores[testId]?.average || 0
        }))
        .sort((a, b) => b.score - a.score)[0];

      const testName = translate ? this.getTranslatedTestName(bestTest.testId, translate) : TEST_METADATA[bestTest.testId].name;
      
      let explanation = '';
      if (bestTest.score >= 90) {
        explanation = translate ? `${bestTest.score}% - ${translate('dashboard.excellentMastery')}` : `${bestTest.score}% - excellent mastery`;
      } else if (bestTest.score >= 80) {
        explanation = translate ? `${bestTest.score}% - ${translate('dashboard.strongPerformance')}` : `${bestTest.score}% - strong performance`;
      } else if (bestTest.score >= 70) {
        explanation = translate ? `${bestTest.score}% - ${translate('dashboard.goodProgress')}` : `${bestTest.score}% - good progress`;
      } else if (bestTest.score > 0) {
        explanation = translate ? `${bestTest.score}% - ${translate('dashboard.buildingSkills')}` : `${bestTest.score}% - building skills`;
      } else {
        explanation = translate ? translate('dashboard.readyToStartLearning') : 'Ready to start learning';
      }

      return {
        message: testName,
        testId: bestTest.testId,
        explanation
      };
    } catch (error) {
      logger.error('Error getting most important achievement:', error);
      // Fallback to strength area
      const testScores = this.getTestScores();
      const bestTest = Object.keys(TEST_METADATA)
        .map(testId => ({
          testId,
          score: testScores[testId]?.average || 0
        }))
        .sort((a, b) => b.score - a.score)[0];

      return {
        message: translate ? this.getTranslatedTestName(bestTest.testId, translate) : TEST_METADATA[bestTest.testId].name,
        testId: bestTest.testId,
        explanation: `${bestTest.score}% - ${translate ? translate('dashboard.strongPerformance') : 'strong performance'}`
      };
    }
  }

  // Calculate overall practice average (for dashboard)
  getPracticeAverage(): number {
    const testScores = this.getTestScores();
    const scores = Object.values(testScores).map(t => t.average);
    
    if (scores.length === 0) return 0;
    
    const average = scores.reduce((a, b) => a + b, 0) / scores.length;
    return Math.round(average);
  }

  // Get mock exam results from localStorage
  getMockExamResults(): TestResult[] {
    const mockExamResults: TestResult[] = [];
    
    // Check for all possible mock exam result keys
    // Mock exams can be saved with various IDs: exam1, exam2, exam3, real-mock-exam, ai-exam, mock-exam1, etc.
    const possibleExamIds = [
      'exam1', 'exam2', 'exam3',
      'mock-exam1', 'mock-exam2', 'mock-exam3',
      'real-mock-exam', 'ai-exam'
    ];
    
    // Check each possible exam ID
    for (const examId of possibleExamIds) {
      const result = localStorage.getItem(`mockExamResults_${examId}`);
      if (result) {
        try {
          const data = JSON.parse(result);
          // Only add if it has valid data
          if (data.percentage !== undefined && data.score !== undefined) {
            mockExamResults.push({
              testId: examId,
              testName: data.examId === 'real-mock-exam' ? 'Standard Exam' : 
                       data.examId === 'ai-exam' ? 'AI Personalised Exam' :
                       `Exam ${examId.replace(/\D/g, '') || '1'}`,
              score: data.score,
              totalQuestions: data.totalQuestions || 50,
              percentage: data.percentage,
              date: new Date().toISOString() // Use current date as approximation
            });
          }
        } catch (error) {
          logger.error(`Error parsing mock exam ${examId} result:`, error);
        }
      }
    }
    
    // Also check all localStorage keys that start with mockExamResults_ (fallback)
    // This catches any other exam IDs we might have missed
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mockExamResults_') && !possibleExamIds.some(id => key === `mockExamResults_${id}`)) {
          const result = localStorage.getItem(key);
          if (result) {
            try {
              const data = JSON.parse(result);
              if (data.percentage !== undefined && data.score !== undefined) {
                const examId = key.replace('mockExamResults_', '');
                // Check if we haven't already added this exam
                if (!mockExamResults.some(r => r.testId === examId)) {
                  mockExamResults.push({
                    testId: examId,
                    testName: examId === 'real-mock-exam' ? 'Mock Exam' : 
                             examId === 'ai-exam' ? 'Personalised Exam' :
                             `Exam ${examId.replace(/\D/g, '') || '1'}`,
                    score: data.score,
                    totalQuestions: data.totalQuestions || 50,
                    percentage: data.percentage,
                    date: new Date().toISOString()
                  });
                }
              }
            } catch (error) {
              logger.error(`Error parsing mock exam result from ${key}:`, error);
            }
          }
        }
      }
    } catch (error) {
      logger.error('Error iterating localStorage for mock exam results:', error);
    }
    
    return mockExamResults;
  }

  // Get combined average (practice tests + mock exams)
  getCombinedAverage(): number {
    const practiceHistory = this.getTestHistory();
    const mockExamResults = this.getMockExamResults();
    
    // Combine all results
    const allResults = [...practiceHistory, ...mockExamResults];
    
    if (allResults.length === 0) return 0;
    
    const totalScore = allResults.reduce((sum, result) => sum + result.percentage, 0);
    return Math.round(totalScore / allResults.length);
  }

  // Get total questions (practice + mock exams)
  getTotalQuestions(): number {
    const practiceHistory = this.getTestHistory();
    const mockExamResults = this.getMockExamResults();
    
    const practiceQuestions = practiceHistory.reduce((sum, result) => sum + result.totalQuestions, 0);
    const mockExamQuestions = mockExamResults.reduce((sum, result) => sum + result.totalQuestions, 0);
    
    return practiceQuestions + mockExamQuestions;
  }

  // Get study time (total time spent in app - tracked from dashboard entry)
  // IMPORTANT: This is for DISPLAY ONLY. AI recommendations use PERFORMANCE, not time.
  getStudyTime(): number {
    // Use actual tracked time from studyTimeTracker (starts when dashboard is entered)
    try {
      const { studyTimeTracker } = require('./studyTimeTracker');
      return studyTimeTracker.getStudyTimeHours();
    } catch (error) {
      // Fallback: estimate from questions if tracker not available
    const history = this.getTestHistory();
    const mockExamResults = this.getMockExamResults();
    const practiceQuestions = history.reduce((sum, result) => sum + result.totalQuestions, 0);
    const mockExamQuestions = mockExamResults.reduce((sum, result) => sum + result.totalQuestions, 0);
    const totalQuestions = practiceQuestions + mockExamQuestions;
      const hours = totalQuestions / 40; // 1.5 min per Q = 40 Q per hour
    return parseFloat(hours.toFixed(1));
    }
  }

  // Mock Exam Unlock System
  canUnlockMockExams(): boolean {
    // ONLY REQUIREMENT: ALL practice tests must score 70% or higher (NO EXCEPTIONS)
    const testHistory = this.getTestHistory();
    
    // IMPORTANT: For first-time users (no test history), this should ALWAYS be false
    const hasAnyTestHistory = testHistory && testHistory.length > 0;
    if (!hasAnyTestHistory) {
      return false; // First-time users should NOT see mock exam
    }
    
    // Check if ANY practice test scored below 70%
    const hasLowScore = testHistory.some(test => test.percentage < 70);
    if (hasLowScore) {
      return false; // 70% requirement NOT met - at least one test below 70%
    }
    
    // All tests scored 70% or higher - unlock mock exams
    return true;
  }

  // AI Exam Unlock System - Production Readiness: Use actual test history (more accurate)
  canUnlockAIExam(): boolean {
    const { aiExamRegenerationService } = require('./aiExamRegenerationService');
    return aiExamRegenerationService.hasSufficientNewLearning();
  }

  // Get unlock progress for dashboard
  getUnlockProgress(): {
    completedTests: number;
    requiredTests: number;
    averageScore: number;
    requiredAverage: number;
    minTestScore: number;
    requiredMinScore: number;
    studyTime: number;
    requiredStudyTime: number;
    canUnlock: boolean;
  } {
    const testHistory = this.getTestHistory();
    const minTestScore = testHistory.length > 0
      ? Math.min(...testHistory.map(test => test.percentage))
      : 0;
    const averageScore = testHistory.length > 0
      ? testHistory.reduce((sum, test) => sum + test.percentage, 0) / testHistory.length
      : 0;
    const { aiExamRegenerationService } = require('./aiExamRegenerationService');
    
    return {
      completedTests: 0,
      requiredTests: 0,
      averageScore: Math.round(averageScore),
      requiredAverage: 0,
      minTestScore: minTestScore,
      requiredMinScore: 0,
      studyTime: this.getStudyTime(),
      requiredStudyTime: 0,
      canUnlock: aiExamRegenerationService.hasSufficientNewLearning()
    };
  }

  // Get mock exam average score
  getMockExamAverage(): number {
    const mockExamResults = this.getMockExamResults();
    if (mockExamResults.length === 0) return 0;
    
    const totalScore = mockExamResults.reduce((sum, result) => sum + result.percentage, 0);
    return Math.round(totalScore / mockExamResults.length);
  }

  // Get mock exam count
  getMockExamCount(): number {
    return this.getMockExamResults().length;
  }

  // Get mock exam pass rate (2025 CBR format - all exams use 88% pass mark, 44/50 correct)
  getMockExamPassRate(): number {
    const mockExamResults = this.getMockExamResults();
    if (mockExamResults.length === 0) return 0;
    
    // All mock exams use 88% pass mark (44/50 questions) - 2025 CBR format
    const passedExams = mockExamResults.filter(result => result.percentage >= 88).length;
    
    return Math.round((passedExams / mockExamResults.length) * 100);
  }

  // Get individual test score
  getTestScore(testId: string): number | null {
    const testScores = this.getTestScores();
    const testData = testScores[testId];
    return testData ? testData.lastScore : null;
  }

  // Smart Study Plan - Game-changing features
  getSmartStudyPlan(translate?: (key: string) => string) {
    const testScores = this.getTestScores();
    const averageScore = this.getCombinedAverage();
    
    // Days remaining is no longer used (exam date selection removed)
    const daysRemaining = 0;
    
    // Find weak areas (scores below 60%)
    const weakAreas = Object.entries(testScores)
      .filter(([_, data]) => data.average < 60)
      .map(([testId, data]) => ({
        testId,
        name: TEST_METADATA[testId]?.name || testId,
        score: data.average,
        lastScore: data.lastScore
      }))
      .sort((a, b) => a.score - b.score);

    // Find strong areas (scores above 80%)
    const strongAreas = Object.entries(testScores)
      .filter(([_, data]) => data.average >= 80)
      .map(([testId, data]) => ({
        testId,
        name: TEST_METADATA[testId]?.name || testId,
        score: data.average
      }));

    // Get today's focus topic
    const todayFocus = this.getTodayFocus(weakAreas, daysRemaining);
    
    // Calculate study time needed
    const studyTimeNeeded = this.calculateStudyTimeNeeded(averageScore, daysRemaining);
    
    // Get exam readiness prediction
    const examPrediction = this.getExamPrediction(averageScore, weakAreas, daysRemaining, translate);

    return {
      todayFocus,
      weakAreas: weakAreas.slice(0, 2), // Top 2 weak areas
      strongAreas: strongAreas.slice(0, 2), // Top 2 strong areas
      studyTimeNeeded,
      daysRemaining,
      examPrediction,
      averageScore
    };
  }

  private getTodayFocus(weakAreas: any[], daysRemaining: number) {
    if (daysRemaining <= 0) return null;
    
    // If less than 7 days, focus on high-frequency topics
    if (daysRemaining < 7) {
      const highFrequencyTopics = ['traffic-lights-signals', 'priority-rules', 'speed-limits'];
      const availableTopics = weakAreas.filter(area => 
        highFrequencyTopics.includes(area.testId)
      );
      return availableTopics[0] || weakAreas[0];
    }
    
    // Otherwise focus on weakest area
    return weakAreas[0];
  }

  private calculateStudyTimeNeeded(averageScore: number, daysRemaining: number) {
    if (daysRemaining <= 0) return 0;
    
    const targetScore = 70; // 70% to be safe
    const scoreGap = Math.max(0, targetScore - averageScore);
    
    // 1 hour study = 5% improvement
    const hoursNeeded = Math.ceil(scoreGap / 5);
    const dailyMinutes = Math.ceil((hoursNeeded * 60) / daysRemaining);
    
    return Math.min(dailyMinutes, 60); // Max 1 hour per day
  }

  private getExamPrediction(averageScore: number, weakAreas: any[], daysRemaining: number, translate?: (key: string) => string) {
    if (daysRemaining <= 0) return null;
    
    // Base prediction on current score
    let confidence = averageScore;
    
    // Adjust based on weak areas
    const criticalWeakAreas = weakAreas.filter(area => area.score < 40);
    confidence -= criticalWeakAreas.length * 5;
    
    // Adjust based on time remaining
    if (daysRemaining < 7) {
      confidence -= 10; // Less time = lower confidence
    } else if (daysRemaining > 30) {
      confidence += 5; // More time = higher confidence
    }
    
    // Cap confidence between 0-100
    confidence = Math.max(0, Math.min(100, confidence));
    
    let message = '';
    if (confidence >= 80) {
      message = `Excellent progress! Keep it up!`;
    } else if (confidence >= 60) {
      message = `Good progress! You're doing well!`;
    } else if (confidence >= 40) {
      message = `Keep practicing to improve!`;
    } else {
      message = translate ? translate('dashboard.startWithBasics') : `Start with the basics and build up!`;
    }
    
    return { confidence: Math.round(confidence), message };
  }

  // Get user's performance per topic
  getTopicPerformance(): Record<string, { average: number; count: number; lastScore: number }> {
    return this.getTestScores();
  }

  // Get weak topics that need practice
  getWeakTopics(threshold: number = 60): string[] {
    const testScores = this.getTestScores();
    const weakTopics: string[] = [];
    
    Object.entries(testScores).forEach(([testId, data]) => {
      if (data.average < threshold) {
        weakTopics.push(testId);
      }
    });

    // Also include unpracticed tests
    Object.keys(TEST_METADATA).forEach(testId => {
      if (!testScores[testId] && !weakTopics.includes(testId)) {
        weakTopics.push(testId);
      }
    });

    return weakTopics;
  }

  // Check if mock exam should be personalized
  shouldPersonalizeMockExam(): boolean {
    const history = this.getTestHistory();
    // Personalize if user has taken at least 3 practice tests
    return history.length >= 3;
  }

  // Get recommended difficulty level for adaptive learning
  getRecommendedDifficulty(): number {
    const history = this.getTestHistory();
    if (history.length === 0) return 3; // Start at beginner-intermediate
    
    const recentTests = history.slice(-5);
    const scores = recentTests.map(test => test.percentage);
    const average = scores.reduce((a, b) => a + b, 0) / scores.length;
    
    // Simple mapping
    if (average >= 90) return 9;
    if (average >= 80) return 7;
    if (average >= 70) return 5;
    if (average >= 60) return 4;
    if (average >= 50) return 3;
    return 2;
  }
}

export const aiCoach = new AICoachService();
